import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
} from "reactstrap";

import { confirmAdminAction } from "admin/contentUtils";
import AdminListControls from "components/admin/AdminListControls";
import AdminPageHeader from "components/admin/AdminPageHeader";
import AdminStateBlock from "components/admin/AdminStateBlock";
import StatCard from "components/StatCard";
import { formatKadekonPhone, kadekonRepo } from "data/repositories/kadekonRepo";

const STATUS_LABEL = {
  pending: "Bekliyor",
  approved: "Onaylandı",
  rejected: "Reddedildi",
  active: "Aktif",
  inactive: "Pasif",
  revoked: "İptal",
  expired: "Süresi doldu",
};

const STATUS_COLOR = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  active: "success",
  inactive: "secondary",
  revoked: "danger",
  expired: "secondary",
};

const SOURCE_LABEL = {
  ios: "iOS",
  android: "Android",
  web: "Web",
};

const TABS = [
  { key: "applications", label: "Başvurular" },
  { key: "members", label: "Üyeler" },
  { key: "qr", label: "QR kodlar" },
];

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("tr-TR");
}

function formatBirthDate(value) {
  if (!value) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split("-");
    return `${day}.${month}.${year}`;
  }
  return value;
}

export default function AdminKadekon() {
  const [tab, setTab] = useState("applications");
  const [applications, setApplications] = useState([]);
  const [members, setMembers] = useState([]);
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [busyId, setBusyId] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [selectedId, setSelectedId] = useState("");
  const [reviewNote, setReviewNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextApplications, nextMembers, nextQr] = await Promise.all([
        kadekonRepo.listApplications(),
        kadekonRepo.listMembers(),
        kadekonRepo.listQrCodes(),
      ]);
      setApplications(nextApplications);
      setMembers(nextMembers);
      setQrCodes(nextQr);
    } catch (loadError) {
      setError(loadError?.message || "KADEKON verileri yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pendingCount = applications.filter((item) => item.status === "pending").length;
  const approvedCount = applications.filter((item) => item.status === "approved").length;
  const activeMembers = members.filter((item) => item.status === "active").length;

  const filteredApplications = useMemo(() => {
    const query = search.trim().toLowerCase();
    return applications.filter((item) => {
      if (statusFilter && item.status !== statusFilter) return false;
      if (!query) return true;
      return [
        item.fullName,
        item.nationalId,
        item.phone,
        item.phoneKey,
        item.email,
        item.birthPlace,
        item.source,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [applications, search, statusFilter]);

  const selected = filteredApplications.find((item) => item.id === selectedId) || filteredApplications[0] || null;

  useEffect(() => {
    if (tab !== "applications") return;
    if (selected && selected.id !== selectedId) {
      setSelectedId(selected.id);
      setReviewNote(selected.reviewNote || "");
    }
    if (!selected) {
      setSelectedId("");
      setReviewNote("");
    }
  }, [selected, selectedId, tab]);

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    return members.filter((item) => {
      if (statusFilter === "active" || statusFilter === "inactive") {
        if (item.status !== statusFilter) return false;
      }
      if (!query) return true;
      return [item.fullName, item.nationalId, item.phone, item.phoneKey, item.email]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [members, search, statusFilter]);

  const filteredQr = useMemo(() => {
    const query = search.trim().toLowerCase();
    return qrCodes.filter((item) => {
      if (statusFilter === "active" || statusFilter === "revoked" || statusFilter === "expired") {
        if (item.status !== statusFilter) return false;
      }
      if (!query) return true;
      return [item.memberName, item.phone, item.phoneKey, item.id].join(" ").toLowerCase().includes(query);
    });
  }, [qrCodes, search, statusFilter]);

  const changeTab = (nextTab) => {
    setTab(nextTab);
    setSearch("");
    setStatusFilter(nextTab === "applications" ? "pending" : "");
    setFlash("");
  };

  const onApprove = async (application) => {
    if (!application) return;
    if (!confirmAdminAction(application.fullName, "üyeliği onaylansın mı? Onay sonrası KADEKON üyesi oluşturulur.")) return;
    setBusyId(application.id);
    setError("");
    try {
      await kadekonRepo.approve(application.id);
      setFlash(`${application.fullName} onaylandı. Üyelik kaydı oluşturuldu.`);
      await load();
    } catch (approveError) {
      setError(approveError?.message || "Başvuru onaylanamadı.");
    } finally {
      setBusyId("");
    }
  };

  const onReject = async (application) => {
    if (!application) return;
    if (!confirmAdminAction(application.fullName, "başvurusu reddedilsin mi?")) return;
    setBusyId(application.id);
    setError("");
    try {
      await kadekonRepo.reject(application.id, reviewNote);
      setFlash(`${application.fullName} başvurusu reddedildi.`);
      await load();
    } catch (rejectError) {
      setError(rejectError?.message || "Başvuru reddedilemedi.");
    } finally {
      setBusyId("");
    }
  };

  const onToggleMember = async (member) => {
    const nextStatus = member.status === "active" ? "inactive" : "active";
    const actionText = nextStatus === "inactive" ? "üyeliği pasife alınsın mı?" : "üyeliği yeniden aktif edilsin mi?";
    if (!confirmAdminAction(member.fullName, actionText)) return;
    setBusyId(member.id);
    setError("");
    try {
      await kadekonRepo.setMemberStatus(member.id, nextStatus);
      setFlash(`${member.fullName} ${nextStatus === "active" ? "aktif" : "pasif"} yapıldı.`);
      await load();
    } catch (statusError) {
      setError(statusError?.message || "Üye durumu güncellenemedi.");
    } finally {
      setBusyId("");
    }
  };

  const onRevokeQr = async (qr) => {
    if (!confirmAdminAction(qr.memberName, "QR kodu iptal edilsin mi?")) return;
    setBusyId(qr.id);
    setError("");
    try {
      await kadekonRepo.revokeQr(qr.id);
      setFlash("QR kod iptal edildi.");
      await load();
    } catch (revokeError) {
      setError(revokeError?.message || "QR kod iptal edilemedi.");
    } finally {
      setBusyId("");
    }
  };

  const applicationFilters = [
    {
      key: "status",
      label: "Durum",
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: "", label: "Tüm durumlar" },
        { value: "pending", label: "Bekleyen" },
        { value: "approved", label: "Onaylanan" },
        { value: "rejected", label: "Reddedilen" },
      ],
    },
  ];

  const memberFilters = [
    {
      key: "status",
      label: "Durum",
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: "", label: "Tüm üyeler" },
        { value: "active", label: "Aktif" },
        { value: "inactive", label: "Pasif" },
      ],
    },
  ];

  const qrFilters = [
    {
      key: "status",
      label: "Durum",
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: "", label: "Tüm kodlar" },
        { value: "active", label: "Aktif" },
        { value: "revoked", label: "İptal" },
      ],
    },
  ];

  return (
    <>
      <AdminPageHeader
        title="KADEKON Club"
        description="Üyelik başvurularını inceleyin, onaylayın veya reddedin. Onaylanan başvuru aktif üye kaydına dönüşür."
        primaryAction={load}
        primaryLabel="Yenile"
      />
      <Container className="mt--6" fluid>
        <Row className="mb-4">
          <Col lg={4} md={6} className="mb-3">
            <StatCard title="Bekleyen başvuru" value={pendingCount} icon="fas fa-hourglass-half" color="warning" />
          </Col>
          <Col lg={4} md={6} className="mb-3">
            <StatCard title="Onaylanan" value={approvedCount} icon="fas fa-check" color="success" />
          </Col>
          <Col lg={4} md={6} className="mb-3">
            <StatCard title="Aktif üye" value={activeMembers} icon="fas fa-id-card" color="info" />
          </Col>
        </Row>

        <Nav tabs className="mb-3">
          {TABS.map((item) => (
            <NavItem key={item.key}>
              <NavLink
                href="#"
                className={tab === item.key ? "active" : ""}
                onClick={(event) => {
                  event.preventDefault();
                  changeTab(item.key);
                }}
              >
                {item.label}
                {item.key === "applications" && pendingCount ? (
                  <Badge color="warning" pill className="ml-2">
                    {pendingCount}
                  </Badge>
                ) : null}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {tab === "applications" ? (
          <Row>
            <Col xl="7" className="mb-4">
              <Card className="shadow">
                <CardHeader>
                  <h3 className="mb-3">Başvuru listesi</h3>
                  <AdminListControls
                    search={search}
                    onSearch={setSearch}
                    searchPlaceholder="Ad, T.C., telefon veya e-posta ara"
                    filters={applicationFilters}
                    sortValue="created-desc"
                    onSortChange={() => {}}
                    sortOptions={[{ value: "created-desc", label: "En yeni" }]}
                    onReset={() => {
                      setSearch("");
                      setStatusFilter("pending");
                    }}
                    resultCount={filteredApplications.length}
                    totalCount={applications.length}
                  />
                </CardHeader>
                <CardBody>
                  <AdminStateBlock
                    flash={flash ? { color: "success", message: flash } : null}
                    onDismissFlash={() => setFlash("")}
                    error={error}
                    onDismissError={() => setError("")}
                    loading={loading}
                    isEmpty={!filteredApplications.length}
                    emptyTitle="Başvuru yok"
                    emptyDescription={statusFilter === "pending" ? "Bekleyen KADEKON başvurusu bulunmuyor." : "Filtrelere uyan başvuru yok."}
                  >
                    <Table responsive hover className="align-items-center mb-0">
                      <thead className="thead-light">
                        <tr>
                          <th>Başvuran</th>
                          <th>Telefon</th>
                          <th>Kaynak</th>
                          <th>Durum</th>
                          <th>Tarih</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredApplications.map((item) => (
                          <tr
                            key={item.id}
                            onClick={() => {
                              setSelectedId(item.id);
                              setReviewNote(item.reviewNote || "");
                            }}
                            style={{ cursor: "pointer", background: selected?.id === item.id ? "rgba(94, 114, 228, 0.08)" : undefined }}
                          >
                            <td>
                              <div className="font-weight-bold">{item.fullName}</div>
                              <small className="text-muted d-block">{item.nationalId || "T.C. yok"}</small>
                            </td>
                            <td>{formatKadekonPhone(item.phoneKey) || item.phone || "—"}</td>
                            <td>{SOURCE_LABEL[item.source] || item.source || "—"}</td>
                            <td>
                              <Badge color={STATUS_COLOR[item.status] || "secondary"} pill>
                                {STATUS_LABEL[item.status] || item.status}
                              </Badge>
                            </td>
                            <td>{formatDateTime(item.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </AdminStateBlock>
                </CardBody>
              </Card>
            </Col>
            <Col xl="5" className="mb-4">
              <Card className="shadow">
                <CardHeader>
                  <h3 className="mb-0">Başvuru onayı</h3>
                  <small className="text-muted">Seçilen başvuruyu inceleyip onaylayın veya reddedin.</small>
                </CardHeader>
                <CardBody>
                  {!selected ? (
                    <p className="text-muted mb-0">Onaylamak için soldan bir başvuru seçin.</p>
                  ) : (
                    <>
                      <h4 className="mb-1">{selected.fullName}</h4>
                      <Badge color={STATUS_COLOR[selected.status] || "secondary"} pill className="mb-3">
                        {STATUS_LABEL[selected.status] || selected.status}
                      </Badge>
                      <dl className="mb-3">
                        <dt>T.C. Kimlik No</dt>
                        <dd>{selected.nationalId || "—"}</dd>
                        <dt>Doğum</dt>
                        <dd>
                          {formatBirthDate(selected.birthDate)}
                          {selected.birthPlace ? ` · ${selected.birthPlace}` : ""}
                        </dd>
                        <dt>Telefon</dt>
                        <dd>{formatKadekonPhone(selected.phoneKey) || selected.phone || "—"}</dd>
                        <dt>E-posta</dt>
                        <dd>{selected.email || "—"}</dd>
                        <dt>Kaynak</dt>
                        <dd>{SOURCE_LABEL[selected.source] || selected.source || "—"}</dd>
                        <dt>Başvuru tarihi</dt>
                        <dd>{formatDateTime(selected.createdAt)}</dd>
                        {selected.reviewedAt ? (
                          <>
                            <dt>İnceleme</dt>
                            <dd>{formatDateTime(selected.reviewedAt)}</dd>
                          </>
                        ) : null}
                        {selected.reviewNote ? (
                          <>
                            <dt>Not</dt>
                            <dd>{selected.reviewNote}</dd>
                          </>
                        ) : null}
                      </dl>
                      {selected.status === "pending" ? (
                        <>
                          <FormGroup>
                            <Label>İnceleme notu (isteğe bağlı, redde eklenir)</Label>
                            <Input
                              type="textarea"
                              rows={3}
                              value={reviewNote}
                              onChange={(event) => setReviewNote(event.target.value)}
                              placeholder="Örn. Kimlik bilgileri eksik"
                            />
                          </FormGroup>
                          <Button
                            color="success"
                            className="mr-2"
                            disabled={Boolean(busyId)}
                            onClick={() => onApprove(selected)}
                          >
                            {busyId === selected.id ? "İşleniyor…" : "Onayla ve üye yap"}
                          </Button>
                          <Button color="danger" outline disabled={Boolean(busyId)} onClick={() => onReject(selected)}>
                            Reddet
                          </Button>
                        </>
                      ) : (
                        <p className="text-muted mb-0">Bu başvuru zaten işlenmiş.</p>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : null}

        {tab === "members" ? (
          <Card className="shadow">
            <CardHeader>
              <h3 className="mb-3">Onaylı üyeler</h3>
              <AdminListControls
                search={search}
                onSearch={setSearch}
                searchPlaceholder="Ad, T.C. veya telefon ara"
                filters={memberFilters}
                sortValue="created-desc"
                onSortChange={() => {}}
                sortOptions={[{ value: "created-desc", label: "En yeni" }]}
                onReset={() => {
                  setSearch("");
                  setStatusFilter("");
                }}
                resultCount={filteredMembers.length}
                totalCount={members.length}
              />
            </CardHeader>
            <CardBody>
              <AdminStateBlock
                flash={flash ? { color: "success", message: flash } : null}
                onDismissFlash={() => setFlash("")}
                error={error}
                onDismissError={() => setError("")}
                loading={loading}
                isEmpty={!filteredMembers.length}
                emptyTitle="Üye yok"
                emptyDescription="Onaylanan başvurular burada üye olarak görünür."
              >
                <Table responsive className="align-items-center">
                  <thead className="thead-light">
                    <tr>
                      <th>Üye</th>
                      <th>Telefon</th>
                      <th>T.C.</th>
                      <th>Durum</th>
                      <th>Kayıt</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="font-weight-bold">{item.fullName}</div>
                          {item.email ? <small className="text-muted d-block">{item.email}</small> : null}
                        </td>
                        <td>{formatKadekonPhone(item.phoneKey) || item.phone || "—"}</td>
                        <td>{item.nationalId || "—"}</td>
                        <td>
                          <Badge color={STATUS_COLOR[item.status] || "secondary"} pill>
                            {STATUS_LABEL[item.status] || item.status}
                          </Badge>
                        </td>
                        <td>{formatDateTime(item.createdAt)}</td>
                        <td className="text-right">
                          <Button size="sm" color={item.status === "active" ? "secondary" : "success"} outline disabled={Boolean(busyId)} onClick={() => onToggleMember(item)}>
                            {item.status === "active" ? "Pasife al" : "Aktif et"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </AdminStateBlock>
            </CardBody>
          </Card>
        ) : null}

        {tab === "qr" ? (
          <Card className="shadow">
            <CardHeader>
              <h3 className="mb-3">Üye QR kodları</h3>
              <AdminListControls
                search={search}
                onSearch={setSearch}
                searchPlaceholder="Üye adı veya telefon ara"
                filters={qrFilters}
                sortValue="created-desc"
                onSortChange={() => {}}
                sortOptions={[{ value: "created-desc", label: "En yeni" }]}
                onReset={() => {
                  setSearch("");
                  setStatusFilter("");
                }}
                resultCount={filteredQr.length}
                totalCount={qrCodes.length}
              />
            </CardHeader>
            <CardBody>
              <AdminStateBlock
                flash={flash ? { color: "success", message: flash } : null}
                onDismissFlash={() => setFlash("")}
                error={error}
                onDismissError={() => setError("")}
                loading={loading}
                isEmpty={!filteredQr.length}
                emptyTitle="QR kod yok"
                emptyDescription="Üyelerin oluşturduğu QR kodlar burada listelenir."
              >
                <Table responsive className="align-items-center">
                  <thead className="thead-light">
                    <tr>
                      <th>Üye</th>
                      <th>Telefon</th>
                      <th>Kod</th>
                      <th>Durum</th>
                      <th>Tarih</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQr.map((item) => (
                      <tr key={item.id}>
                        <td className="font-weight-bold">{item.memberName}</td>
                        <td>{formatKadekonPhone(item.phoneKey) || item.phone || "—"}</td>
                        <td>
                          <small>{item.id}</small>
                        </td>
                        <td>
                          <Badge color={STATUS_COLOR[item.status] || "secondary"} pill>
                            {STATUS_LABEL[item.status] || item.status}
                          </Badge>
                        </td>
                        <td>{formatDateTime(item.createdAt)}</td>
                        <td className="text-right">
                          {item.status === "active" ? (
                            <Button size="sm" color="danger" outline disabled={Boolean(busyId)} onClick={() => onRevokeQr(item)}>
                              İptal et
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </AdminStateBlock>
            </CardBody>
          </Card>
        ) : null}
      </Container>
    </>
  );
}
